<script lang="ts">
    import { onDestroy } from 'svelte';
    import { get } from 'svelte/store';
    import { push } from 'svelte-spa-router';
    import { currentUser, authReady } from '$lib/stores/auth';
    import {
        adminListDeliveries,
        adminUploadDelivery,
        adminDriveCheck,
        humanizeError,
        ApiError,
        type ConsultantDelivery,
    } from '$lib/api';
    import AdminNav from '../components/AdminNav.svelte';

    // A prop, not a constant, so the test can poll fast without fake timers.
    export let pollMs = 3000;

    let deliveries: ConsultantDelivery[] = [];
    let driveConfigured = false;
    let loading = true;
    let error: string | null = null;

    let fileInput: HTMLInputElement | null = null;
    let uploading = false;
    let uploadMessage: string | null = null;
    let uploadError: string | null = null;

    let driveBusy = false;
    let driveMessage: string | null = null;
    let driveError: string | null = null;

    let pollTimer: ReturnType<typeof setInterval> | null = null;
    // Set in onDestroy. A load() awaiting the network when the component is
    // torn down otherwise resumes afterward and can start a poll interval
    // nothing will ever clear (see syncPolling below).
    let destroyed = false;

    // An expired session mid-import would otherwise loop the poll forever on
    // 401s. Count consecutive load() failures and give up on the interval
    // after 3, or immediately on a 401 (the session is gone, retrying on a
    // timer cannot fix that). The Retry button (see retry() below) clears
    // both and tries again right away.
    let consecutiveFailures = 0;
    let pollSuspended = false;

    async function load() {
        // Wait for the initial /api/me before judging the session, same as
        // AdminStatus: the store's first emission is null.
        if (!get(authReady)) return;
        if (!$currentUser || $currentUser.role !== 'admin') {
            push('/map');
            return;
        }
        try {
            const resp = await adminListDeliveries();
            if (destroyed) return;
            deliveries = resp.items;
            driveConfigured = resp.drive_configured;
            error = null;
            consecutiveFailures = 0;
            pollSuspended = false;
        } catch (e: any) {
            if (destroyed) return;
            error = humanizeError(e, 'Failed to load deliveries');
            consecutiveFailures += 1;
            const sessionExpired = e instanceof ApiError && e.status === 401;
            if (sessionExpired || consecutiveFailures >= 3) {
                pollSuspended = true;
                if (pollTimer !== null) {
                    clearInterval(pollTimer);
                    pollTimer = null;
                }
            }
        } finally {
            if (!destroyed) loading = false;
        }
        syncPolling();
    }

    function retry() {
        consecutiveFailures = 0;
        pollSuspended = false;
        load();
    }

    function syncPolling() {
        if (destroyed || pollSuspended) {
            if (pollTimer !== null) {
                clearInterval(pollTimer);
                pollTimer = null;
            }
            return;
        }
        const anyRunning = deliveries.some((d) => d.status === 'running');
        if (anyRunning && pollTimer === null) {
            pollTimer = setInterval(load, pollMs);
        } else if (!anyRunning && pollTimer !== null) {
            clearInterval(pollTimer);
            pollTimer = null;
        }
    }

    async function upload() {
        if (uploading) return;
        const file = fileInput?.files?.[0];
        if (!file) {
            uploadMessage = null;
            uploadError = 'Choose a CSV file first.';
            return;
        }
        uploading = true;
        uploadError = null;
        uploadMessage = null;
        try {
            await adminUploadDelivery(file);
            uploadMessage = 'Import started';
            if (fileInput) fileInput.value = '';
            await load();
        } catch (e: any) {
            uploadError = humanizeError(e, 'Upload failed');
        } finally {
            uploading = false;
        }
    }

    async function checkDrive() {
        if (driveBusy) return;
        driveBusy = true;
        driveError = null;
        driveMessage = null;
        try {
            const result = await adminDriveCheck();
            const base = `Imported ${result.imported}, skipped ${result.skipped}, errors ${result.errors}`;
            if (result.errors > 0) {
                driveError = base;
            } else {
                driveMessage = base;
            }
            await load();
        } catch (e: any) {
            driveError = humanizeError(e, 'Drive check failed');
        } finally {
            driveBusy = false;
        }
    }

    const unsubUser = currentUser.subscribe(() => { load(); });
    const unsubReady = authReady.subscribe(() => { load(); });
    onDestroy(() => {
        destroyed = true;
        unsubUser();
        unsubReady();
        if (pollTimer !== null) clearInterval(pollTimer);
    });

    // received_at is the delivery's vintage, not a real instant: a leading
    // YYYYMMDD in the filename at 00:00 UTC (or the Drive createdTime, or
    // now() as a last resort). Formatting it in the viewer's local zone can
    // print the day before the filename's date, so render it as a bare UTC
    // date instead.
    function fmtDate(s: string | null): string {
        if (!s) return 'none';
        return new Date(s).toLocaleDateString([], {
            timeZone: 'UTC',
            year: 'numeric', month: 'short', day: 'numeric',
        });
    }

    function fmtNum(n: number): string {
        return n.toLocaleString();
    }

    const STATUS_BADGE: Record<string, string> = {
        ok: 'badge-ok',
        error: 'badge-error',
        running: 'badge-warn',
    };
</script>

<div class="admin-page">
    <header class="admin-header">
        <span class="eyebrow">Admin, data deliveries</span>
        <h1>Data deliveries</h1>
        <p class="page-sub">Consultant voter files: uploads, the daily Drive poll, and what each import changed.</p>
    </header>

    <AdminNav active="deliveries" />

    <section class="block">
        <div class="block-head"><h2>Add a delivery</h2></div>
        <div class="upload-row">
            <label class="file-label" for="delivery-file">Delivery CSV</label>
            <input
                id="delivery-file"
                type="file"
                accept=".csv"
                bind:this={fileInput}
                on:change={() => { uploadMessage = null; uploadError = null; }}
            />
            <button class="btn btn-primary" on:click={upload} disabled={uploading}>
                {uploading ? 'Uploading' : 'Upload'}
            </button>
        </div>
        {#if uploadMessage}<p class="msg msg-ok">{uploadMessage}</p>{/if}
        {#if uploadError}<p class="msg msg-error">{uploadError}</p>{/if}

        <div class="upload-row">
            <button class="btn" on:click={checkDrive} disabled={driveBusy || !driveConfigured}>
                {driveBusy ? 'Checking' : 'Check Drive now'}
            </button>
            {#if !driveConfigured}
                <span class="muted">Set CANVASS_CONSULTANT_DRIVE_FOLDER_ID and CANVASS_SHEETS_SA_JSON_PATH to enable</span>
            {/if}
        </div>
        <p class="muted drive-hint">A check that finds a new file can take a few minutes. The table below updates on its own; do not click again.</p>
        {#if driveMessage}<p class="msg msg-ok">{driveMessage}</p>{/if}
        {#if driveError}<p class="msg msg-error">{driveError}</p>{/if}
    </section>

    <section class="block">
        <div class="block-head"><h2>Deliveries</h2></div>
        {#if error}
            <p class="msg msg-error banner-row">
                <span>{error}</span>
                <button class="btn" on:click={retry}>Retry</button>
            </p>
        {/if}
        {#if loading}
            <p class="loading">Loading</p>
        {:else if deliveries.length === 0}
            <p class="muted">No deliveries imported yet.</p>
        {:else}
            <div class="table-wrap">
                <table>
                    <thead>
                        <tr>
                            <th>Received</th>
                            <th>Source</th>
                            <th>File</th>
                            <th>Status</th>
                            <th class="num">Rows</th>
                            <th class="num" title="Rows not stored: blank ncid, or a duplicate ncid superseded by a later row">Skipped</th>
                            <th class="num">Matched</th>
                            <th class="num">Unmatched</th>
                            <th class="num">Phones filled</th>
                            <th>Error</th>
                        </tr>
                    </thead>
                    <tbody>
                        {#each deliveries as d (d.id)}
                            <tr>
                                <td class="mono">{fmtDate(d.received_at)}</td>
                                <td>{d.source}</td>
                                <td class="file-cell">{d.filename}</td>
                                <td><span class="badge {STATUS_BADGE[d.status] ?? ''}">{d.status}</span></td>
                                <td class="num mono">{fmtNum(d.row_count)}</td>
                                <td class="num mono">{d.skipped_count > 0 ? fmtNum(d.skipped_count) : ''}</td>
                                <td class="num mono">{fmtNum(d.matched_count)}</td>
                                <td class="num mono">{d.unmatched_count > 0 ? fmtNum(d.unmatched_count) : ''}</td>
                                <td class="num mono">{fmtNum(d.phones_filled)}</td>
                                <td class="error-cell">{d.error_message ?? ''}</td>
                            </tr>
                        {/each}
                    </tbody>
                </table>
            </div>
        {/if}
    </section>
</div>

<style>
    .admin-page {
        max-width: 1080px;
        margin: 0 auto;
        padding: var(--s-7) var(--s-5) var(--s-8);
        color: var(--ink);
    }
    .admin-header { margin-bottom: var(--s-5); }
    .admin-header .eyebrow {
        color: var(--leaf-bright);
        display: block;
        margin-bottom: var(--s-2);
    }
    .admin-header h1 {
        font-family: var(--font-display);
        font-size: clamp(2rem, 5vw, 2.8rem);
        margin: 0 0 var(--s-2);
        font-weight: 600;
    }
    .page-sub { color: var(--ink-soft); font-size: 1rem; margin: 0; }

    .block { margin-top: var(--s-7); }
    .block-head {
        display: flex;
        align-items: baseline;
        justify-content: space-between;
        gap: var(--s-3);
        margin-bottom: var(--s-4);
        padding-bottom: var(--s-3);
        border-bottom: 1px solid var(--rule);
    }
    .block-head h2 {
        font-family: var(--font-display);
        font-size: 1.3rem;
        font-weight: 500;
        margin: 0;
    }

    .upload-row {
        display: flex;
        align-items: center;
        gap: var(--s-3);
        flex-wrap: wrap;
        margin-bottom: var(--s-3);
    }
    .file-label {
        font-size: 0.72rem;
        text-transform: uppercase;
        letter-spacing: 0.1em;
        color: var(--ink-muted);
        font-weight: 600;
    }

    .table-wrap {
        background: var(--field);
        border: 1px solid var(--rule);
        overflow-x: auto;
    }
    table { width: 100%; border-collapse: collapse; font-size: 0.9rem; }
    th, td { text-align: left; padding: 12px 14px; border-bottom: 1px solid var(--rule); }
    tbody tr:last-child td { border-bottom: none; }
    tbody tr:hover { background: var(--paper-soft); }
    th {
        font-size: 0.72rem;
        text-transform: uppercase;
        letter-spacing: 0.1em;
        color: var(--ink-muted);
        font-weight: 600;
        background: var(--paper);
        border-bottom: 2px solid var(--rule);
    }
    th.num, td.num { text-align: right; }
    .mono { font-family: var(--font-mono); }
    .file-cell { max-width: 260px; word-break: break-all; }

    .badge {
        display: inline-block;
        padding: 3px 10px;
        font-size: 0.72rem;
        font-weight: 600;
        letter-spacing: 0.04em;
        text-transform: lowercase;
        border: 1px solid;
    }
    .badge-ok    { background: var(--leaf-soft);  color: var(--leaf-ink); border-color: var(--leaf-bright); }
    .badge-error { background: var(--alert-soft); color: var(--alert);    border-color: var(--alert); }
    .badge-warn  { background: var(--sun-soft);   color: var(--ink);      border-color: var(--sun); }

    .error-cell { color: var(--alert); max-width: 240px; word-break: break-word; font-size: .78rem; }

    .btn {
        font-family: var(--font-body);
        font-size: 0.85rem;
        font-weight: 600;
        padding: 8px 16px;
        cursor: pointer;
        border: 1px solid var(--ink);
        background: var(--paper);
        color: var(--ink);
        transition: background 0.15s, color 0.15s;
    }
    .btn-primary { background: var(--ink); color: var(--paper); }
    .btn:hover:not(:disabled) { background: var(--leaf); border-color: var(--leaf); color: var(--paper); }
    .btn:disabled { opacity: 0.5; cursor: not-allowed; }

    .msg {
        margin: 0 0 var(--s-3);
        padding: 10px 14px;
        font-size: 0.9rem;
        border-left: 3px solid;
    }
    .msg-ok    { background: var(--leaf-soft);  border-color: var(--leaf-bright); color: var(--leaf-ink); }
    .msg-error { background: var(--alert-soft); border-color: var(--alert);       color: var(--alert); }

    .muted      { color: var(--ink-muted); font-style: italic; font-size: 0.9rem; }
    .drive-hint { margin: 0 0 var(--s-3); }
    .loading    { color: var(--ink-soft); font-size: 0.95rem; padding: var(--s-5) 0; }

    .banner-row {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: var(--s-3);
    }
</style>
